import {useEffect} from 'react'
import {useRouter} from 'next/router'
import {getTrustArcConsent} from '@/services/trustarc'
import {logMessage} from '@/services/logger/client'

declare global {
	interface Window {
		bazaarVoiceLoaded?: boolean
	}
}

interface BazaarVoiceCoordinatorProps {
	bazaarVoiceLocale: string | null
	environment: 'production' | 'staging'
}

/**
 * Triggers BazaarVoice to re-scan DOM for widgets after client-side navigation
 */
function triggerBazaarVoiceRescan(win: Window): void {
	if (!win.BV?.ui) return

	try {
		const {ui} = win.BV
		// Runtime type check for BazaarVoice API - ui.display is optional and dynamically loaded

		if ('display' in ui && typeof ui.display === 'function') {
			ui.display()
		}
	} catch (error) {
		const errorMessage =
			error instanceof Error ? error.message : String(error)
		logMessage(`BazaarVoice re-scan failed: ${errorMessage}`)
	}
}

/**
 * Loads the BazaarVoice SDK script
 */
function loadBazaarVoiceScript(
	win: Window,
	environment: 'production' | 'staging',
	locale: string
): void {
	const script = document.createElement('script')
	script.id = 'bazaarVoice'
	script.type = 'text/javascript'
	script.src = `https://apps.bazaarvoice.com/deployments/elanco/main_site/${environment}/${locale}/bv.js`
	script.async = true

	script.onload = () => {
		if (!win.BV) {
			logMessage(
				'BazaarVoice SDK loaded but window.BV is not available; removing script for retry'
			)
			script.remove()
			win.bazaarVoiceLoaded = false
			return
		}
		const consent = getTrustArcConsent()
		const shouldTrack =
			consent === 'ALL_COOKIES' || consent === 'ADVERTISE_COOKIES'
		logMessage(`Bazaar voice tracking is enabled: ${shouldTrack}`)
		win.BV.cookieConsent.setConsent({
			BVBRANDID: shouldTrack,
			bv_metrics: shouldTrack,
			BVBRANDSID: shouldTrack,
		})
		win.bazaarVoiceLoaded = true
	}

	script.onerror = () => {
		logMessage('Failed to load BazaarVoice SDK')
		// Remove the failed script so the next render/navigation is not
		// blocked by the existingScript guard.
		script.remove()
		win.bazaarVoiceLoaded = false
	}

	document.head.appendChild(script)
	win.bazaarVoiceLoaded = true
}

/**
 * Coordinator component that loads BazaarVoice SDK only when needed.
 * Detects the presence of BazaarVoice blocks on the page (ratings or reviews)
 * and loads the BazaarVoice SDK script.
 * Only loads for Pet US pages with BazaarVoice widgets.
 */
export function BazaarVoiceCoordinator({
	bazaarVoiceLocale,
	environment,
}: BazaarVoiceCoordinatorProps): null {
	const router = useRouter()

	useEffect(() => {
		const win = globalThis as typeof globalThis & Window
		if (!bazaarVoiceLocale) return

		const hasBazaarVoiceWidget = document.querySelector('[data-bv-show]')
		if (!hasBazaarVoiceWidget) return

		// If SDK is already loaded, trigger re-scan for SPA navigation
		if (win.bazaarVoiceLoaded && win.BV) {
			triggerBazaarVoiceRescan(win)
			return
		}

		if (win.bazaarVoiceLoaded) return

		// Guard against in-flight requests
		const existingScript = document.querySelector(
			'script[id="bazaarVoice"]'
		)
		if (existingScript) return

		loadBazaarVoiceScript(win, environment, bazaarVoiceLocale)
	}, [bazaarVoiceLocale, environment, router.asPath])

	return null
}
